import React from 'react';
import { Row, Col, Timeline } from 'antd';
import {
  getFlightDetailsByAircraftId,
  getFlightlogsInitialLoad,
  getSelectedFlightAircraftId,
  getSelectedFlightLegacyEvents,
  getSelectedFlightLogs
} from '../../../redux/selectors/flyingPage';
import { useSelector } from 'react-redux';
import { getDateFormat, getTimezone } from '../../../redux/selectors/userData';
import { RED, SOS } from '../../Flying/AircraftList/constants';
import { UIFlightLogs, UILegacyEvent } from '../../../types/flyingPage';
import { ExpandedFlightDetailHeader } from './ExpandedFlightDetailHeader';
import { FlightLogsLoadingTimeLineItem } from './LoadingTimelineItem';
import { FlightLogsTimelineItem } from './FlightLogsTimelineItem';
import { ActivePointDetailLineItem } from './ActivePointDetailLineItem';
import { LegacyEventTimelineItem } from './LegacyEventTimelineItem';

const FIFTEEN_MINUTES = 15 * 60 * 1000;
export const ONE_HOUR = 60 * 60 * 1000;
const minHeight = '48px';

interface ExpandedFlightDetailItemProps {
  aircraftId: string;
}

function isFlightActive(latestPointTime: number): boolean {
  if (Date.now() - latestPointTime >= FIFTEEN_MINUTES) {
    return false;
  }
  return true;
}

const ExpandedFlightDetailItem: React.FC<ExpandedFlightDetailItemProps> = ({ aircraftId }) => {
  const selectedAircraftId = useSelector(getSelectedFlightAircraftId);
  const flightDetails = useSelector(getFlightDetailsByAircraftId(aircraftId));

  const dateFormat = useSelector(getDateFormat);
  const timeFormat = 'HH:mm';
  const timezone = useSelector(getTimezone);
  const activeSOS = flightDetails?.aircraft.trackStatus === SOS;

  const flightlogsEvents = useSelector(getSelectedFlightLogs);
  const initialLoadComplete = useSelector(getFlightlogsInitialLoad);

  const legacyEvents = useSelector(getSelectedFlightLegacyEvents);

  if (!flightDetails) {
    return <></>;
  }
  const latestPoint = flightDetails.track.points[flightDetails.track.points.length - 1];
  if (!latestPoint) {
    return <></>;
  }

  const flightActive = isFlightActive(latestPoint.timestamp);

  const addDurationToEvent = (event: UIFlightLogs, flightActive: boolean): boolean => {
    if (
      (event.eventName.match(/Blocks On/) && flightActive) ||
      (event.eventName.match(/Blocks Off/) && flightActive)
    ) {
      console.log('returning false');
      return false;
    }
    return true;
  };

  const getFLEventsToRender = (
    startEvent: UIFlightLogs | undefined,
    endEvent: UIFlightLogs | undefined,
    takeoffAndLandingEvents: UIFlightLogs[]
  ) => {
    const finalEvents: UIFlightLogs[] = [];
    const lastNonEndEvent =
      takeoffAndLandingEvents.length > 0
        ? takeoffAndLandingEvents[takeoffAndLandingEvents.length - 1]
        : startEvent;

    if (startEvent) {
      if (!endEvent)
        finalEvents.push({
          eventId: startEvent.eventId,
          eventName: startEvent.eventName,
          eventDescription: startEvent.eventDescription,
          eventTime: startEvent.eventTime,
          durationMillis: addDurationToEvent(startEvent, flightActive)
            ? Math.max(latestPoint.timestamp - startEvent.eventTime, 0)
            : undefined
        });
      else {
        finalEvents.push(startEvent);
      }
    }

    if (takeoffAndLandingEvents.length > 0) {
      finalEvents.push(...takeoffAndLandingEvents);
    }

    if (endEvent) {
      if (lastNonEndEvent && endEvent.eventTime > lastNonEndEvent.eventTime) {
        finalEvents.push({
          eventId: endEvent.eventId,
          eventName: endEvent.eventName,
          eventDescription: endEvent.eventDescription,
          eventTime: endEvent.eventTime,
          durationMillis: addDurationToEvent(endEvent, flightActive)
            ? endEvent.durationMillis
            : undefined
        });
      }
    }

    return finalEvents;
  };

  const getLegacyEventsToRender = (
    startEvent: UILegacyEvent,
    endEvent: UILegacyEvent,
    speedUpOrDownEvents: UILegacyEvent[]
  ) => {
    if (flightActive) {
      return [startEvent, ...speedUpOrDownEvents];
    }
    return [startEvent, ...speedUpOrDownEvents, endEvent];
  };

  const flightlogsAvailable =
    flightlogsEvents !== undefined &&
    (flightlogsEvents.startEvent !== undefined ||
      flightlogsEvents.endEvent !== undefined ||
      flightlogsEvents.takeoffAndLandingEvents.length > 0);

  const firstPoint = flightDetails.track.points[0];
  const lastPoint = flightDetails.track.points[flightDetails.track.points.length - 1];

  let flightlogsItems: UIFlightLogs[] = [];
  let legacyEventsItems: UILegacyEvent[] = [];

  if (flightlogsAvailable) {
    flightlogsItems = getFLEventsToRender(
      flightlogsEvents.startEvent,
      flightlogsEvents.endEvent,
      flightlogsEvents.takeoffAndLandingEvents
    );
  }

  // no legacy event, no flightlogs after the initial load, we have nothing to show
  if (legacyEvents === undefined && initialLoadComplete && !flightlogsAvailable) {
    return <></>;
  }

  if (legacyEvents) {
    legacyEventsItems = getLegacyEventsToRender(
      legacyEvents.startEvent,
      legacyEvents.endEvent,
      legacyEvents.speedUpOrDownEvents
    );
  }

  return (
    <Row
      className={`flight-detail-list-item ${
        selectedAircraftId === aircraftId
          ? activeSOS
            ? 'selected-list-item-sos'
            : 'selected-list-item'
          : ''
      }`}
      //Required for the StatusV2 component to be vertically centered
      style={{ minHeight, color: activeSOS ? RED : undefined }}
    >
      <Col span={24}>
        <ExpandedFlightDetailHeader flightDetail={flightDetails} flightActive={flightActive} />
        <Row style={{ marginTop: '8px' }}>
          <Col offset={1} span={23}>
            <Timeline
              className={`compact-timeline ${flightActive ? 'active-point-detail' : ''}`}
              style={{ marginBottom: '5px' }}
            >
              {!initialLoadComplete && (
                <FlightLogsLoadingTimeLineItem
                  firstPoint={firstPoint}
                  lastPoint={lastPoint}
                  dateFormat={dateFormat}
                  timeFormat={timeFormat}
                  timezone={timezone}
                  isActive={flightActive}
                />
              )}
              {initialLoadComplete &&
                flightlogsAvailable &&
                flightlogsItems.map(item => (
                  <FlightLogsTimelineItem
                    flightLogs={item}
                    flightActive={flightActive}
                    dateFormat={dateFormat}
                    timeFormat={timeFormat}
                    timezone={timezone}
                    key={item.eventId.toString()}
                  />
                ))}
              {initialLoadComplete &&
                !flightlogsAvailable &&
                legacyEventsItems.map(event => (
                  <LegacyEventTimelineItem
                    flightActive={flightActive}
                    legacyEvent={event}
                    dateFormat={dateFormat}
                    timeFormat={timeFormat}
                    timezone={timezone}
                    key={event.eventTime.toString()}
                  />
                ))}
              {flightActive && (
                <ActivePointDetailLineItem
                  aircraftId={aircraftId}
                  firstPoint={firstPoint}
                  latestPoint={latestPoint}
                  activeSOS={activeSOS}
                  dateFormat={dateFormat}
                  timeFormat={timeFormat}
                  timezone={timezone}
                />
              )}
            </Timeline>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
export default ExpandedFlightDetailItem;
